// shop/articles.js — выбор конкретного экземпляра перед добавлением в корзину
// =============================================================================
// «В корзину» больше не кладёт товар сразу: сначала открывается окно с лентой
// фотографий, и покупатель выбирает физическую вещь. В корзину уходит её номер.
//
// Разметка окна — партиал sm-ArticlePicker, наполнение собирается здесь:
// изделий на странице много, а окно одно.

import * as cart from './cart.js';
import * as router from './router.js';

function q(sel, root) { return (root || document).querySelector(sel); }

function el(tag, options, children) {
  const node = document.createElement(tag);
  const opts = options || {};
  if (opts.class) node.className = opts.class;
  if (opts.text != null) node.textContent = opts.text;
  if (opts.attrs) Object.keys(opts.attrs).forEach(k => node.setAttribute(k, opts.attrs[k]));
  (children || []).forEach(c => node.appendChild(c));
  return node;
}

let root = null;
let currentSlug = '';
// 'articles' — выбор экземпляра, 'corporate' — корпоративный заказ,
// 'masterclass' — запись на мастер-класс. Режим сбрасывается при закрытии.
let mode = 'articles';

function slide(product, article) {
  const inCart = cart.qtyOf(product.slug, article.id) > 0;

  const button = el('button', {
    class: 'crb-partial-sm-picker-option',
    attrs: {
      type: 'button',
      'data-article-id': article.id,
      'aria-label': product.name + ', экземпляр № ' + article.id +
        (inCart ? ' — уже в корзине' : ''),
    },
  }, [
    el('img', {
      // В окне выбора кадры квадратные: так в ряд помещаются три штуки
      // и ни один не обрезается по-разному.
      attrs: {
        src: article.square || article.image,
        width: '600',
        height: '600',
        loading: 'lazy',
        decoding: 'async',
        alt: product.name + ' — экземпляр № ' + article.id,
      },
    }),
    el('span', { class: 'crb-partial-sm-picker-code', text: '№ ' + article.id }),
    el('span', {
      class: 'crb-partial-sm-picker-state',
      text: inCart ? 'в корзине' : 'выбрать',
    }),
  ]);

  if (inCart) button.disabled = true;

  const slideEl = el('li', {
    class: 'crb-partial-sm-picker-slide' + (inCart ? ' is-chosen' : ''),
  }, [button]);

  if (inCart) {
    // Заметная отметка вместо бледной подписи: сразу видно, что вещь уже взята.
    slideEl.appendChild(el('span', { class: 'crb-partial-sm-picker-badge', text: '✓ В корзине' }));

    // Крестик в углу — убрать экземпляр из корзины, не уходя со страницы.
    slideEl.appendChild(el('button', {
      class: 'crb-partial-sm-picker-remove',
      attrs: {
        type: 'button',
        'data-article-remove': article.id,
        'aria-label': 'Убрать из корзины экземпляр № ' + article.id,
        title: 'Убрать из корзины',
      },
    }, [
      (() => {
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('viewBox', '0 0 24 24');
        svg.setAttribute('width', '16');
        svg.setAttribute('height', '16');
        svg.setAttribute('fill', 'none');
        svg.setAttribute('stroke', 'currentColor');
        svg.setAttribute('stroke-width', '2.5');
        svg.setAttribute('stroke-linecap', 'round');
        svg.setAttribute('aria-hidden', 'true');
        const p = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        p.setAttribute('d', 'M18 6 6 18M6 6l12 12');
        svg.appendChild(p);
        return svg;
      })(),
    ]));
  }

  return slideEl;
}

// ── Корпоративный заказ и мастер-класс ──────────────────────────────────────
// Общая часть: карточка выбранного изделия слева, действие справа.
function productCard(product) {
  return el('div', { class: 'crb-partial-sm-picker-alt-product' }, [
    el('img', {
      attrs: {
        src: product.image,
        width: '800',
        height: '1000',
        loading: 'lazy',
        decoding: 'async',
        alt: product.alt || product.name,
      },
    }),
    el('div', {}, [
      el('p', { class: 'crb-partial-sm-picker-alt-name', text: product.name }),
      el('p', { class: 'crb-partial-sm-picker-alt-price', text: cart.formatPrice(product.price) + ' за изделие' }),
    ]),
  ]);
}

// Контакты справа от формы: те же данные, что в окне «Написать мастеру»,
// меняется только нижняя подсказка — она зависит от повода обращения.
function contactsAside(note) {
  return el('aside', { class: 'crb-partial-sm-contact-info' }, [
    el('h3', { class: 'crb-partial-sm-contact-info-title', text: 'Контакты' }),
    el('dl', { class: 'crb-partial-sm-contact-info-list' }, [
      el('div', {}, [el('dt', { text: 'Телефон' }), el('dd', {}, [el('a', { text: '+7 (3462) 00-00-00', attrs: { href: 'tel:+73462000000' } })])]),
      el('div', {}, [el('dt', { text: 'Telegram' }), el('dd', {}, [el('a', { text: '@smola_surgut', attrs: { href: 'https://t.me/smola_surgut', target: '_blank', rel: 'nofollow noopener' } })])]),
      el('div', {}, [el('dt', { text: 'Мастерская' }), el('dd', { text: 'г. Сургут, ХМАО — Югра' })]),
      el('div', {}, [el('dt', { text: 'Время работы' }), el('dd', { text: 'Ежедневно, 10:00–21:00' })]),
    ]),
    el('p', { class: 'crb-partial-sm-contact-info-note', text: note }),
  ]);
}

// Корпоративный заказ и мастер-класс устроены одинаково: карточка изделия
// и заявка слева, контакты справа. Отличаются только текстом и полями,
// поэтому блок собирается из общей заготовки.
// Классы формы взяты у окна «Написать мастеру» — вид тот же, своих стилей
// заводить не нужно.
function enquiryBlock(product, spec) {
  const field = (name, label, type, placeholder, required) => {
    const id = spec.prefix + '-' + name;
    const input = el('input', { attrs: { id, name, type, placeholder: placeholder || '' } });
    if (required) input.required = true;
    return el('div', { class: 'crb-partial-sm-contact-field' }, [
      el('label', { text: label, attrs: { for: id } }),
      input,
    ]);
  };

  const form = el('form', {
    class: 'crb-partial-sm-contact-form',
    attrs: { 'data-picker-form': spec.prefix },
  }, [
    el('h3', { class: 'crb-partial-sm-picker-alt-title', text: spec.title }),
    el('p', { class: 'crb-partial-sm-picker-alt-text', text: spec.text }),
    ...spec.fields.map(f => field(f[0], f[1], f[2], f[3], f[4])),
  ]);

  const consentId = spec.prefix + '-consent';
  const consentInput = el('input', { attrs: { id: consentId, name: 'consent', type: 'checkbox' } });
  consentInput.required = true;

  const consentLabel = el('label', { attrs: { for: consentId } });
  consentLabel.appendChild(document.createTextNode('Согласен на обработку персональных данных в соответствии с '));
  consentLabel.appendChild(el('a', { text: 'политикой конфиденциальности', attrs: { href: '/privacy.html', target: '_blank', rel: 'noopener' } }));
  consentLabel.appendChild(document.createTextNode('.'));

  form.appendChild(el('div', { class: 'crb-partial-sm-contact-consent' }, [consentInput, consentLabel]));
  form.appendChild(el('button', { class: 'crb-partial-sm-contact-submit', text: spec.submit, attrs: { type: 'submit' } }));
  form.appendChild(el('p', { class: 'crb-partial-sm-contact-status', attrs: { 'data-picker-status': '', role: 'status', hidden: 'hidden' } }));

  return el('div', { class: 'crb-partial-sm-picker-alt-grid' }, [
    el('div', {}, [productCard(product), form]),
    contactsAside(spec.note),
  ]);
}

function corporateBlock(product) {
  return enquiryBlock(product, {
    prefix: 'co',
    title: 'Корпоративный заказ',
    text: 'Партия одинаковых изделий: подарки сотрудникам, клиентам или к мероприятию. '
      + 'Экземпляр выбирать не нужно — сделаю нужное количество в одном оттенке, '
      + 'с вашим логотипом на упаковке. От 10 штук — цена ниже розничной.',
    fields: [
      ['name', 'Как вас зовут', 'text', 'Анна', true],
      ['company', 'Компания', 'text', 'ООО «Сургутнефтегаз»'],
      ['phone', 'Телефон или мессенджер', 'tel', '+7 900 000-00-00', true],
      ['quantity', 'Сколько изделий', 'number', '30'],
      ['deadline', 'К какой дате', 'text', 'К 8 марта'],
    ],
    submit: 'Отправить заявку',
    note: 'Обсудить партию быстрее всего — в Telegram.',
  });
}

function questionBlock(product) {
  return enquiryBlock(product, {
    prefix: 'pq',
    title: 'Вопрос об изделии',
    text: 'Размер, оттенок, длина цепочки, сроки, повтор в другом цвете — '
      + 'спрашивайте о чём угодно по этому изделию. Отвечу в течение дня.',
    fields: [
      ['name', 'Как вас зовут', 'text', 'Анна', true],
      ['phone', 'Телефон или мессенджер', 'tel', '+7 900 000-00-00', true],
      ['question', 'Ваш вопрос', 'text', 'Можно сделать цепочку длиннее?'],
    ],
    submit: 'Отправить вопрос',
    note: 'Быстрее всего отвечу в Telegram.',
  });
}

function masterclassBlock(product) {
  return enquiryBlock(product, {
    prefix: 'mc',
    title: 'Мастер-класс',
    text: 'Сделаете такое изделие сами, под моим присмотром: смола, пигменты, '
      + 'формы и фурнитура мои. Два часа, от одного до шести человек — '
      + 'подойдёт и для компании, и для подарка.',
    fields: [
      ['name', 'Как вас зовут', 'text', 'Анна', true],
      ['phone', 'Телефон или мессенджер', 'tel', '+7 900 000-00-00', true],
      ['when', 'Когда удобно', 'text', 'Суббота днём'],
    ],
    submit: 'Записаться',
    note: 'Записаться быстрее всего — в Telegram.',
  });
}

// Блок и надзаголовок для каждого режима, кроме выбора экземпляра.
const ALT_MODES = {
  corporate:   { eyebrow: 'Корпоративный заказ', block: corporateBlock },
  masterclass: { eyebrow: 'Мастер-класс',        block: masterclassBlock },
  contact:     { eyebrow: 'Вопрос мастеру',      block: questionBlock },
};

function renderAlt(product) {
  const alt = q('[data-picker-alt]', root);
  const carousel = q('[data-carousel]', root);
  const lead = q('[data-picker-lead]', root);
  const hint = q('[data-picker-hint]', root);
  const eyebrow = q('[data-picker-eyebrow]', root);
  if (!alt) return;

  const isArticles = mode === 'articles';
  if (eyebrow) {
    eyebrow.textContent = isArticles ? 'Выберите экземпляр' : ALT_MODES[mode].eyebrow;
  }
  alt.hidden = isArticles;
  if (carousel) carousel.hidden = !isArticles;

  each('[data-picker-mode]', btn => {
    const active = btn.getAttribute('data-picker-mode') === mode;
    btn.classList.toggle('is-active', active);
    btn.setAttribute('aria-pressed', String(active));
  });

  if (isArticles) {
    alt.replaceChildren();
    if (lead) lead.hidden = false;
    return;
  }

  if (lead) lead.hidden = true;
  if (hint) hint.textContent = '';
  alt.replaceChildren(ALT_MODES[mode].block(product));
}

function each(selector, fn, scope) {
  (scope || root).querySelectorAll(selector).forEach(fn);
}

function render(product) {
  const name = q('[data-picker-name]', root);
  const list = q('[data-picker-list]', root);
  const hint = q('[data-picker-hint]', root);

  if (name) name.textContent = product.name;

  renderAlt(product);
  if (mode !== 'articles') return;
  if (!list) return;

  const articles = Array.isArray(product.articles) ? product.articles : [];
  list.replaceChildren(...articles.map(a => slide(product, a)));

  // Лента длиннее трёх кадров — включаем перетаскивание и центрирование.
  const carousel = q('[data-carousel]', root);
  if (carousel) {
    carousel.classList.toggle('is-scrollable', articles.length > 3);
  }

  if (hint) {
    const free = articles.filter(a => cart.qtyOf(product.slug, a.id) === 0).length;
    hint.textContent = articles.length > 1
      ? `Доступно экземпляров: ${free} из ${articles.length}. Цена одинаковая — ${cart.formatPrice(product.price)}.`
      : `Этот экземпляр остался один. ${cart.formatPrice(product.price)}.`;
  }

}

// Embla меряет ширины по факту: пока окно скрыто, все размеры нулевые и
// перетаскивание не работает. Пересчитываем после показа, а не до него.
// Embla грузится лениво — по первому движению мыши или касанию, то есть уже
// после открытия окна. Ждём появления экземпляра, иначе настройка ленты
// (подписки, стартовый кадр) выполнялась бы «в пустоту».
function whenEmbla(carousel, callback, tries = 40) {
  if (carousel._embla) return callback(carousel._embla);
  if (tries <= 0) return;
  setTimeout(() => whenEmbla(carousel, callback, tries - 1), 150);
}

function remeasure() {
  const carousel = q('[data-carousel]', root);
  if (!carousel) return;

  syncNav();
  whenEmbla(carousel, embla => setupCarousel(carousel, embla));
}

function setupCarousel(carousel, embla) {

  // Подписку вешаем сразу, а не внутри requestAnimationFrame: в фоновой
  // вкладке кадры не отрисовываются, rAF не срабатывает — и стрелки
  // остались бы навсегда в исходном состоянии.
  if (!carousel._navBound) {
    embla.on('select', syncNav);
    embla.on('reInit', syncNav);
    carousel._navBound = true;
  }

  // Пересчёт размеров — после того как окно показано и получило ширину.
  setTimeout(() => {
    embla.reInit();
    // Открываемся не на первом кадре: при центрировании слева осталась бы
    // пустота. Со второго кадра лента заполнена, а по краям видны соседние.
    const snaps = embla.scrollSnapList().length;
    if (snaps > 3) embla.scrollTo(1, true);
    syncNav();
  }, 0);

  syncNav();
}

// Стрелки гаснут на краях ленты — иначе непонятно, что дальше ничего нет.
function syncNav() {
  const carousel = q('[data-carousel]', root);
  const prev = q('[data-picker-prev]', root);
  const next = q('[data-picker-next]', root);
  if (!prev || !next) return;

  const embla = carousel && carousel._embla;
  if (embla) {
    prev.disabled = !embla.canScrollPrev();
    next.disabled = !embla.canScrollNext();
    return;
  }

  // Пока Embla не загрузилась, лента прокручивается нативно.
  const viewport = q('[data-carousel-viewport]', root);
  if (!viewport) return;
  prev.disabled = viewport.scrollLeft <= 0;
  next.disabled = viewport.scrollLeft + viewport.clientWidth >= viewport.scrollWidth - 1;
}

function step(direction) {
  const carousel = q('[data-carousel]', root);
  const embla = carousel && carousel._embla;

  if (embla) {
    direction > 0 ? embla.scrollNext() : embla.scrollPrev();
    return;
  }

  // Запасной путь без Embla: прокручиваем на ширину кадра.
  const viewport = q('[data-carousel-viewport]', root);
  const slide = q('.crb-partial-sm-picker-slide', root);
  if (!viewport || !slide) return;
  viewport.scrollBy({ left: direction * slide.getBoundingClientRect().width, behavior: 'smooth' });
  setTimeout(syncNav, 400);
}

// wanted — режим, с которого открываем: 'articles' (по умолчанию),
// 'corporate', 'masterclass' или 'contact'.
export function open(slug, wanted) {
  if (!root) return false;

  const product = cart.findProduct(slug);
  if (!product || !Array.isArray(product.articles) || !product.articles.length) return false;

  currentSlug = slug;
  mode = ALT_MODES[wanted] ? wanted : 'articles';
  render(product);

  // Пока App.init не отработал, окна ещё нет — тогда добавляем первый
  // свободный экземпляр, чтобы кнопка не молчала.
  if (!root._modal) {
    if (mode !== 'articles') return false;
    const free = product.articles.find(a => cart.qtyOf(slug, a.id) === 0);
    if (free) cart.add(slug, free.id);
    return true;
  }

  root._modal.show();
  remeasure();
  router.pushState(mode === 'articles' ? 'select' : mode, { slug: currentSlug });
  return true;
}

function whenModal(callback, tries = 40) {
  if (root && root._modal) return callback();
  if (tries <= 0) return;
  setTimeout(() => whenModal(callback, tries - 1), 100);
}

// Открытие из адреса: /catalog/<slug>/<состояние>.
export function openFromRoute(state) {
  if (!root || !state || !state.slug) return;

  const product = cart.findProduct(state.slug);
  if (!product) return;

  currentSlug = state.slug;
  mode = ALT_MODES[state.kind] ? state.kind : 'articles';
  render(product);

  // Окно поднимает компонент библиотеки на App.init(). Порядок запуска
  // модулей не гарантирован, поэтому ждём появления его API, иначе адрес
  // открывался бы с правильным содержимым, но невидимым окном.
  whenModal(() => {
    root._modal.show();
    remeasure();
  });

  // Адрес экземпляра — подводим ленту к нужному кадру.
  if (state.kind === 'article' && state.id) {
    const index = (product.articles || []).findIndex(a => String(a.id) === String(state.id));
    const carousel = q('[data-carousel]', root);
    if (index > -1 && carousel) {
      whenEmbla(carousel, embla => setTimeout(() => embla.scrollTo(index, true), 0));
    }
  }
}

export function close() {
  if (root && root._modal) root._modal.hide();
}

function choose(articleId) {
  if (!currentSlug) return;
  cart.add(currentSlug, articleId);

  // Экземпляр выбран — сразу показываем корзину: покупателю видно, что
  // именно он взял, и дальше идти к оформлению.
  if (root._modal) root._modal.hide();
  window.location.href = '/cart.html';
}

export function init() {
  root = q('.crb-partial-sm-picker');
  if (!root) return;

  root.addEventListener('click', e => {
    const modeBtn = e.target.closest('[data-picker-mode]');
    if (modeBtn) {
      const wanted = modeBtn.getAttribute('data-picker-mode');
      // Повторное нажатие снимает режим и возвращает выбор экземпляра.
      mode = (mode === wanted && wanted !== 'articles') ? 'articles' : wanted;
      const product = cart.findProduct(currentSlug);
      if (product) render(product);
      if (mode === 'articles') remeasure();
      // Режим — часть адреса: его можно прислать ссылкой.
      router.pushState(mode === 'articles' ? 'select' : mode, { slug: currentSlug });
      return;
    }

    const removeBtn = e.target.closest('[data-article-remove]');
    if (removeBtn) {
      cart.remove(currentSlug, removeBtn.getAttribute('data-article-remove'));
      const p = cart.findProduct(currentSlug);
      if (p) render(p);
      return;
    }

    if (e.target.closest('[data-picker-prev]')) return step(-1);
    if (e.target.closest('[data-picker-next]')) return step(1);

    const option = e.target.closest('[data-article-id]');
    if (!option || option.disabled) return;
    choose(option.getAttribute('data-article-id'));
  });

  // Нативная прокрутка (пока нет Embla) тоже должна гасить стрелки.
  const viewport = q('[data-carousel-viewport]', root);
  if (viewport) viewport.addEventListener('scroll', syncNav, { passive: true });

  // Закрытие окна возвращает адрес карточки товара.
  const closeBtn = q('[data-modal-close]', root);
  if (closeBtn) closeBtn.addEventListener('click', () => router.pushState('none'));

  // Заявка на мастер-класс и на корпоративный заказ: приёма заявок ещё нет,
  // поэтому форма честно говорит об этом и отправляет в Telegram, как и
  // остальные формы сайта.
  root.addEventListener('submit', e => {
    const form = e.target.closest('[data-picker-form]');
    if (!form) return;
    e.preventDefault();

    const status = q('[data-picker-status]', form);
    const show = (text) => { if (status) { status.textContent = text; status.hidden = false; } };

    if (!form.checkValidity()) {
      show('Заполните имя, контакт и подтвердите согласие.');
      form.reportValidity();
      return;
    }

    show(form.getAttribute('data-picker-form') === 'co'
      ? 'Приём заявок пока не подключён. Напишите в Telegram — посчитаю смету и сроки.'
      : 'Приём заявок пока не подключён. Напишите в Telegram — запишу на ближайшую дату.');
  });

  // Корзину могли изменить из другой вкладки или с самой страницы.
  cart.subscribe(() => {
    if (!currentSlug) return;
    const product = cart.findProduct(currentSlug);
    if (product) render(product);
  });
}
